import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Linking, Alert, Platform } from 'react-native';
import { Text, Button, Card, ActivityIndicator, Divider } from 'react-native-paper';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionService } from '../services/subscriptionService';
import { logger } from '../utils/logger';

const SubscriptionScreen: React.FC = () => {
  const { user, subscriptionStatus, isSubscribed, refreshSubscription } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (): Promise<void> => {
    try {
      setLoading(true);
      logger.info('Starting subscription flow');

      const { url } = await subscriptionService.createCheckoutSession(
        'https://oceaninsightpromobile.web.app/',
        'https://oceaninsightpromobile.web.app/'
      );

      logger.info('Opening Stripe Checkout:', url);

      // Open Stripe Checkout
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        throw new Error('Unable to open checkout page');
      }
    } catch (error) {
      logger.error('Failed to start subscription', error);
      Alert.alert(
        'Subscription Error',
        'Failed to start subscription process. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async (): Promise<void> => {
    try {
      setLoading(true);
      logger.info('Opening billing portal');

      const { url } = await subscriptionService.createPortalSession(
        'https://oceaninsightpromobile.web.app/'
      );

      logger.info('Opening Stripe Portal:', url);

      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        throw new Error('Unable to open billing portal');
      }
    } catch (error) {
      logger.error('Failed to open billing portal', error);
      Alert.alert(
        'Error',
        'Failed to open billing portal. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date?: Date | any): string => {
    if (!date) return 'N/A';
    // Handle Firestore Timestamp objects
    const dateObj = date?.toDate ? date.toDate() : date;
    if (!dateObj || !(dateObj instanceof Date)) return 'N/A';
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!user) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Please log in to view subscription</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Card style={styles.card}>
        <Card.Title
          title="Ocean Insight Pro"
          subtitle="Premium Subscription"
          titleStyle={styles.cardTitle}
        />
        <Card.Content>
          {isSubscribed ? (
            <View>
              <Text style={styles.statusText}>Status: Active</Text>
              <Text style={styles.infoText}>
                Your subscription is currently active. You have full access to all premium features.
              </Text>

              <Divider style={styles.divider} />

              <View style={styles.detailsContainer}>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Status:</Text>
                  <Text style={styles.detailValue}>
                    {subscriptionStatus.status.charAt(0).toUpperCase() +
                      subscriptionStatus.status.slice(1)}
                  </Text>
                </View>

                {subscriptionStatus.currentPeriodEnd && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Renews:</Text>
                    <Text style={styles.detailValue}>
                      {formatDate(subscriptionStatus.currentPeriodEnd)}
                    </Text>
                  </View>
                )}

                {subscriptionStatus.cancelAtPeriodEnd && (
                  <View style={styles.detailRow}>
                    <Text style={styles.warningText}>
                      Your subscription will be canceled at the end of the current period.
                    </Text>
                  </View>
                )}
              </View>

              <Button
                mode="contained"
                onPress={handleManageSubscription}
                loading={loading}
                disabled={loading}
                style={styles.button}
                icon="open-in-new"
              >
                Manage Subscription
              </Button>

              <Text style={styles.manageInfoText}>
                Use the billing portal to:
                {'\n'}• Cancel your subscription
                {'\n'}• Update payment method
                {'\n'}• View billing history
                {'\n'}• Download invoices
              </Text>
            </View>
          ) : (
            <View>
              <Text style={styles.statusText}>Status: Not Subscribed</Text>
              <Text style={styles.infoText}>
                Subscribe to Ocean Insight Pro to unlock all premium features:
              </Text>

              <View style={styles.featuresContainer}>
                <Text style={styles.featureItem}>• AI-powered fishing hotspot predictions</Text>
                <Text style={styles.featureItem}>• Near real-time satellite oceanographic data</Text>
                <Text style={styles.featureItem}>• SST, Chlorophyll & Current analysis</Text>
                <Text style={styles.featureItem}>• Advanced weather forecasts</Text>
                <Text style={styles.featureItem}>• Unlimited catch reports & trip planning</Text>
                <Text style={styles.featureItem}>• GPX export for marine electronics</Text>
              </View>

              <Divider style={styles.divider} />

              <View style={styles.pricingContainer}>
                <Text style={styles.priceText}>$24.99/month</Text>
                <Text style={styles.priceSubtext}>Cancel anytime</Text>
              </View>

              <Button
                mode="contained"
                onPress={handleSubscribe}
                loading={loading}
                disabled={loading}
                style={styles.button}
              >
                Subscribe Now
              </Button>

              <Text style={styles.disclaimerText}>
                By subscribing, you agree to automatic recurring billing until canceled.
              </Text>
            </View>
          )}
        </Card.Content>
      </Card>

      <Card style={styles.card}>
        <Card.Title title="Need Help?" />
        <Card.Content>
          <Text style={styles.infoText}>
            If you have any questions about your subscription or billing, please contact our
            support team.
          </Text>
        </Card.Content>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  content: {
    padding: 16,
    paddingBottom: 32,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  statusText: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#2196F3',
  },
  infoText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    lineHeight: 20,
  },
  divider: {
    marginVertical: 16,
  },
  detailsContainer: {
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  detailLabel: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  detailValue: {
    fontSize: 14,
    color: '#333',
  },
  warningText: {
    fontSize: 14,
    color: '#FF9800',
    fontWeight: '500',
    marginTop: 8,
  },
  featuresContainer: {
    marginTop: 12,
    marginBottom: 16,
  },
  featureItem: {
    fontSize: 14,
    color: '#333',
    marginBottom: 8,
    lineHeight: 20,
  },
  pricingContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  priceText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#2196F3',
  },
  priceSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  button: {
    marginTop: 8,
    marginBottom: 12,
  },
  disclaimerText: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    marginTop: 8,
  },
  manageInfoText: {
    fontSize: 13,
    color: '#666',
    marginTop: 16,
    lineHeight: 22,
    backgroundColor: '#F5F5F5',
    padding: 12,
    borderRadius: 8,
  },
  errorText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginTop: 24,
  },
});

export default SubscriptionScreen;
